import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CloseOutlined, EditOutlined, MailOutline, SaveOutlined } from "@mui/icons-material";
import SurfaceCard from "../common/ui/SurfaceCard";
import FormField from "../common/ui/FormField";
import ErrorAlert from "../common/ErrorAlert";
import UserProfile from "../../models/user/UserProfile";
import { useError } from "../../error/useError";
import { useUserProfileContext } from "../../contexts/ProfileContext";
import { validateInternationalName } from "../../utils/nameValidation";

const PersonalInfoSection: React.FC = () => {
  const theme = useTheme();

  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { error, helpText, setError, clearError, handleError } = useError();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const { userProfile, refetchProfile, updateProfile, isSavingProfile } = useUserProfileContext();

  const hasName = name.trim().length > 0;
  const profileCompleteness = hasName && email ? 100 : hasName || email ? 60 : 20;
  const profileStatus = profileCompleteness >= 100 ? "Complete" : profileCompleteness >= 60 ? "Good" : "Needs attention";

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name);
      setEmail(userProfile.email);
    }
  }, [userProfile]);

  const handleSave = () => {
    const nameError = validateInternationalName(name);
    if (nameError) {
      setError(nameError);
      return;
    }

    const updatedProfile: UserProfile = {
      email: userProfile?.email || email,
      name,
      currency: userProfile?.currency || "USD",
      startingBalance: userProfile?.startingBalance || 0,
    };

    updateProfile(updatedProfile, {
      onSuccess: () => {
        setSuccess("Personal information updated successfully!");
        setEditing(false);
        refetchProfile();
      },
      onError: (apiError) => {
        handleError(apiError);
      },
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setName(userProfile?.name || "");
    clearError();
  };

  return (
    <Box sx={{ p: { xs: 1.2, md: 1.8 } }}>
      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Profile Error"
      />

      <SurfaceCard
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 2.6 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2.4 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Identity Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update how your name appears in your account.
              </Typography>
            </Box>

            {!editing ? (
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => setEditing(true)}
                sx={{
                  textTransform: "none",
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  color: theme.palette.primary.light,
                }}
              >
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  onClick={handleSave}
                  disabled={isSavingProfile || !name.trim()}
                  sx={{ textTransform: "none" }}
                >
                  {isSavingProfile ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloseOutlined />}
                  onClick={handleCancel}
                  disabled={isSavingProfile}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>

          <FormField
            fullWidth
            label="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!editing}
            required
            placeholder="Enter your full name"
            inputProps={{ maxLength: 100 }}
            helperText={editing ? `${name.length}/100 characters` : "Your name appears on profile and account screens."}
            sx={{ mb: 2.2 }}
          />

          <FormField
            fullWidth
            label="Email"
            value={email}
            disabled
            helperText="Email changes are handled from the Security section."
            sx={{ mb: 1.4 }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <MailOutline fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                Status: {profileStatus} ({profileCompleteness}%)
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Need to change email? Use the Security section.
            </Typography>
          </Stack>
        </Box>
      </SurfaceCard>

      <Snackbar
        open={!!success}
        autoHideDuration={4200}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PersonalInfoSection;
